import { apiSlice } from "@/shared/api/api-slice"
import type { SuccessResponse } from "@/shared/api/types"

import { i18n, type LocalizedText } from "@/shared/i18n"
import { isObject } from "@/shared/lib"

import type { DeleteSafeListTriggerRequest, DeleteSafeListTriggerResponse } from "./types"
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query"

const SAFE_LIST_DELETE_ERROR_MESSAGES: Record<string, LocalizedText> = {
  safe_list_snapshot_stale: {
    key: "safe-list-delete-stale",
    fallback: "This rule changed. The list was refreshed."
  },
  safe_list_item_not_found: {
    key: "safe-list-delete-not-found",
    fallback: "This rule no longer exists. The list was refreshed."
  },
  safe_list_surface_mismatch: {
    key: "safe-list-delete-surface-mismatch",
    fallback: "This rule belongs to another list. The list was refreshed."
  },
  safe_list_ambiguous_locator: {
    key: "safe-list-delete-ambiguous",
    fallback: "This rule changed. The list was refreshed."
  }
}

const getSafeListErrorCode = (error: FetchBaseQueryError): string | undefined => {
  if (!isObject(error.data) || !Array.isArray(error.data.error)) return undefined
  const firstError: unknown = error.data.error[0]
  if (!isObject(firstError) || typeof firstError.code !== "string") return undefined
  return firstError.code
}

const transformSafeListDeleteError = (error: FetchBaseQueryError): FetchBaseQueryError => {
  const code = getSafeListErrorCode(error)
  const message = code === undefined ? undefined : SAFE_LIST_DELETE_ERROR_MESSAGES[code]
  if (!message) return error

  return {
    status: "CUSTOM_ERROR",
    error: i18n.t(message.key, message.fallback),
    data: { code }
  }
}

export const deleteSafeListTriggerApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: ["SafeList", "SecurityRules", "SecurityRule", "FeaturesStatus"]
  })
  .injectEndpoints({
    endpoints: builder => ({
      deleteSafeListTrigger: builder.mutation<DeleteSafeListTriggerResponse, DeleteSafeListTriggerRequest>({
        query: ({ chatId, surface, index, fingerprint }) => ({
          url: `/api/v1/security/safe-list/chat/${chatId}/${surface}/${index}`,
          method: "DELETE",
          body: { fingerprint }
        }),
        transformResponse: (response: SuccessResponse<null>) => response.result,
        transformErrorResponse: transformSafeListDeleteError,
        async onQueryStarted({ chatId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled
          } catch {
            dispatch(
              apiSlice.util.invalidateTags([
                { type: "SafeList", id: chatId },
                { type: "SecurityRules", id: chatId }
              ])
            )
          }
        },
        invalidatesTags: (_result, error, { chatId }) =>
          error
            ? []
            : [
                { type: "SafeList" as const, id: chatId },
                { type: "SecurityRules" as const, id: chatId },
                { type: "SecurityRule" as const, id: `${chatId}-trigger_words` },
                { type: "FeaturesStatus" as const, id: chatId }
              ]
      })
    })
  })

export const { useDeleteSafeListTriggerMutation } = deleteSafeListTriggerApi
